import React, { useRef, useEffect, useCallback } from 'react';
import { VideoPlayer } from './VideoPlayer';
import { NewNGJob } from '../types';
import styles from '../NewNG.module.css';

interface TheatreModeProps {
  jobId: string;
  networkName?: string;
  jobs?: NewNGJob[];
  currentJob?: NewNGJob | null;
  onJobChange?: (jobId: string) => void;
  onVideoEnd?: () => void;
  isJobsLoading?: boolean;
  onClose?: () => void;
}

export const TheatreMode: React.FC<TheatreModeProps> = ({
  jobId,
  networkName = 'FEI',
  jobs,
  currentJob,
  onJobChange,
  onVideoEnd,
  isJobsLoading = false,
  onClose
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const blurVideoRef = useRef<HTMLVideoElement>(null);
  const frameWrapperRef = useRef<HTMLDivElement>(null);
  const videoSectionRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoContentRef = useRef<HTMLDivElement>(null);
  const listenersAttachedRef = useRef(false);

  // Locate the embedded NewNG <video> element
  const getEmbeddedVideo = useCallback((): HTMLVideoElement | null => {
    const root = containerRef.current;
    if (!root) return null;
    // Use a more specific selector that doesn't rely on CSS class names
    return root.querySelector('video') as HTMLVideoElement | null;
  }, []);

  // Mirror size from NewNG's internal container so our wrapper always tracks it
  const mirrorInnerContainerSize = useCallback(() => {
    const outer = containerRef.current;
    const frameWrapper = frameWrapperRef.current;
    if (!outer || !frameWrapper) return;

    // Get the actual dimensions from the container
    const { width, height } = outer.getBoundingClientRect();
    if (width <= 0 || height <= 0) return;

    const w = Math.round(width) + 'px';
    const h = Math.round(height) + 'px';

    if (outer.style.width !== w || outer.style.height !== h) {
      requestAnimationFrame(() => {
        outer.style.width = w;
        outer.style.height = h;
        frameWrapper.style.width = w;
        frameWrapper.style.height = h;
      });
    }
  }, []);

  // Compute and set size using a known aspect (fallback when metadata is not yet ready)
  const applySizeFromAspect = useCallback((aspect: number) => {
    const container = containerRef.current;
    const frameWrapper = frameWrapperRef.current;
    if (!container || !frameWrapper) return;

    const parentW = container.parentElement?.clientWidth || window.innerWidth;
    const parentH = container.parentElement?.clientHeight || window.innerHeight;
    const maxW = Math.min(window.innerWidth, parentW);
    const maxH = Math.min(window.innerHeight, parentH);

    let width = maxW;
    let height = width / aspect;
    if (height > maxH) {
      height = maxH;
      width = height * aspect;
    }
    const w = Math.round(width) + 'px';
    const h = Math.round(height) + 'px';
    container.style.width = w;
    container.style.height = h;
    frameWrapper.style.width = w;
    frameWrapper.style.height = h;
  }, []);

  // Keep blur video fully in sync with embedded player (src, time, rate, paused)
  const syncBlurToMain = useCallback(() => {
    const main = getEmbeddedVideo();
    const blur = blurVideoRef.current;
    if (!main || !blur) return;

    // Ensure same source
    const mainSrc = (main.currentSrc || main.src || '').toString();
    if (mainSrc && blur.src !== mainSrc) {
      try {
        blur.src = mainSrc;
        blur.load();
      } catch (_) {
        // ignore invalid src transitions
      }
    }

    // Mirror playback attributes
    blur.muted = true;
    blur.loop = true;
    blur.playbackRate = main.playbackRate || 1.0;

    // Keep time close (avoid constant seeking jitter)
    if (Number.isFinite(main.currentTime) && Number.isFinite(blur.currentTime)) {
      const drift = Math.abs(blur.currentTime - main.currentTime);
      if (drift > 0.2) {
        try { blur.currentTime = main.currentTime; } catch (_) {}
      }
    }

    // Mirror play/pause
    if (main.paused) {
      if (!blur.paused) {
        try { blur.pause(); } catch (_) {}
      }
    } else {
      if (blur.paused) {
        blur.play().catch(() => {});
      }
    }
  }, [getEmbeddedVideo]);

  // Dynamic video container sizing based on aspect ratio
  const resizeVideoContainer = useCallback(() => {
    const video = getEmbeddedVideo();
    const container = containerRef.current;
    const frameWrapper = frameWrapperRef.current;

    if (!video || !container || !frameWrapper || !video.videoWidth || !video.videoHeight) return;

    const aspect = video.videoWidth / video.videoHeight;
    const parentW = container.parentElement?.clientWidth || window.innerWidth;
    const parentH = container.parentElement?.clientHeight || window.innerHeight;
    const maxW = Math.min(window.innerWidth, parentW);
    const maxH = Math.min(window.innerHeight, parentH);

    let width = maxW;
    let height = width / aspect;

    if (height > maxH) {
      height = maxH;
      width = height * aspect;
    }

    const w = Math.round(width) + 'px';
    const h = Math.round(height) + 'px';
    container.style.width = w;
    container.style.height = h;
    frameWrapper.style.width = w;
    frameWrapper.style.height = h;
  }, [getEmbeddedVideo]);

  useEffect(() => {
    if (!jobId) {
      if (blurVideoRef.current) blurVideoRef.current.src = '';
      return;
    }

    // Try to locate embedded video and attach listeners/resizers
    const onMeta = () => { syncBlurToMain(); resizeVideoContainer(); };
    const onPlay = () => syncBlurToMain();
    const onPause = () => syncBlurToMain();
    const onSeek = () => { syncBlurToMain(); resizeVideoContainer(); };
    const onRate = () => syncBlurToMain();
    const onTimeUpdate = () => syncBlurToMain();

    const tryAttach = () => {
      if (listenersAttachedRef.current) return;
      const main = getEmbeddedVideo();
      if (!main) return;
      main.addEventListener('loadedmetadata', onMeta);
      main.addEventListener('canplay', onMeta);
      main.addEventListener('play', onPlay);
      main.addEventListener('pause', onPause);
      main.addEventListener('seeking', onSeek);
      main.addEventListener('ratechange', onRate);
      main.addEventListener('timeupdate', onTimeUpdate);
      listenersAttachedRef.current = true;
      // Initial pass
      onMeta();
      mirrorInnerContainerSize();
    };

    const attachInterval = setInterval(tryAttach, 200);

    // Observe container subtree for video replacement/class changes and size accordingly
    let observer: MutationObserver | null = null;
    const startObserver = () => {
      const target = containerRef.current;
      if (!target) return;
      observer = new MutationObserver(() => {
        const vid = getEmbeddedVideo();
        if (!vid) return;

        // Fast path: if class indicates orientation, compute immediately (no metadata needed)
        // Since we can't rely on CSS class names anymore, we'll use video dimensions
        const isPortrait = vid.videoWidth && vid.videoHeight && vid.videoHeight > vid.videoWidth;
        const assumedAspect = isPortrait ? 9 / 16 : 16 / 9;
        applySizeFromAspect(assumedAspect);

        // Also try a few times to size from actual metadata once available
        let tries = 0;
        const id = setInterval(() => {
          tries += 1;
          syncBlurToMain();
          resizeVideoContainer();
          if (tries >= 10) clearInterval(id);
        }, 100);
      });
      observer.observe(target, { attributes: true, childList: true, subtree: true });
    };
    startObserver();

    // Periodic correction to avoid drift
    const interval = setInterval(syncBlurToMain, 250);

    return () => {
      const main = getEmbeddedVideo();
      if (main) {
        main.removeEventListener('loadedmetadata', onMeta);
        main.removeEventListener('canplay', onMeta);
        main.removeEventListener('play', onPlay);
        main.removeEventListener('pause', onPause);
        main.removeEventListener('seeking', onSeek);
        main.removeEventListener('ratechange', onRate);
        main.removeEventListener('timeupdate', onTimeUpdate);
      }
      clearInterval(attachInterval);
      if (observer) observer.disconnect();
      clearInterval(interval);
    };
  }, [jobId, getEmbeddedVideo, syncBlurToMain, mirrorInnerContainerSize, applySizeFromAspect, resizeVideoContainer]);

  // Resize on orientationchange as an extra safety net
  useEffect(() => {
    const handler = () => {
      // defer to allow DOM to settle
      setTimeout(() => {
        const video = getEmbeddedVideo();
        if (video) {
          // Trigger the same resize logic
          const event = new Event('loadedmetadata');
          video.dispatchEvent(event);
        }
      }, 100);
    };
    window.addEventListener('orientationchange', handler);
    return () => window.removeEventListener('orientationchange', handler);
  }, [getEmbeddedVideo]);

  // Resize on video load and window resize
  useEffect(() => {
    const video = getEmbeddedVideo();
    const handleLoadedMetadata = () => resizeVideoContainer();
    if (video) {
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
    }
    window.addEventListener('resize', resizeVideoContainer);

    return () => {
      if (video) video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      window.removeEventListener('resize', resizeVideoContainer);
    };
  }, [resizeVideoContainer, getEmbeddedVideo]);

  return (
    <div className={styles['newng-theatre-bin']}>
      {/* Background blur video remains unchanged to preserve theatre look */}
      <video
        ref={blurVideoRef}
        className={styles['newng-bg-blur-video']}
        autoPlay
        loop
        muted={true}
        playsInline
      />

      {/* Main video container with NewNG player inside existing wrapper */}
      <div className={styles['newng-theatre-video-container']} ref={containerRef}>
        <div className={styles['newng-theatre-frame-wrapper']} ref={frameWrapperRef}>
          <div className={styles['newng-video-section']} ref={videoSectionRef} style={{ width: '100%', height: '100%' }}>
            <div className={styles['newng-video-container']} ref={videoContainerRef} style={{ width: '100%', height: '100%' }}>
              <div className={styles['newng-video-content']} ref={videoContentRef} style={{ width: '100%', height: '100%' }}>
                <VideoPlayer
                  jobId={jobId || ''}
                  onHeightChange={() => {}}
                  controlsMode="full"
                  externalMuted={false}
                  externalVolume={1.0}
                  onVideoEnd={onVideoEnd}
                  scheduleJobs={jobs}
                  scheduleCurrentJob={currentJob}
                  theatreJobs={jobs}
                  theatreCurrentJob={currentJob}
                  onJobSelect={onJobChange}
                  isJobsLoading={isJobsLoading}
                  showLogo={true}
                  showJobSelector={true}
                  showCloseButton={true}
                  onClose={onClose}
                />
              </div>
            </div>
          </div>

          {/* Theatre overlay remains for non-player UI (job selector/back button/logo) */}
          <div className={styles['newng-theatre-overlay']}></div>
        </div>
      </div>
    </div>
  );
};
